// Lazy loader for the Google Maps JavaScript API, without adding a runtime dependency.
// Follows Google's "dynamic library import" bootstrap: a single script tag with
// `loading=async` and a global callback, then `google.maps.importLibrary()` per library.
// https://developers.google.com/maps/documentation/javascript/load-maps-js-api
import { GOOGLE_MAPS_API_KEY } from '@/env';

const CALLBACK_NAME = '__isdGoogleMapsLoaded';

let bootstrapPromise: Promise<void> | null = null;

/**
 * Inject the Maps JavaScript API script once and resolve when it is ready.
 * Subsequent calls reuse the same promise.
 * @returns {Promise<void>}
 */
function bootstrapGoogleMaps(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Google Maps can only be loaded in the browser'));
      return;
    }
    // The API may already be on the page (e.g. after a hot reload)
    const existingMaps = (
      window as { google?: { maps?: { importLibrary?: unknown } } }
    ).google?.maps;
    if (typeof existingMaps?.importLibrary === 'function') {
      resolve();
      return;
    }
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is not set'));
      return;
    }
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      v: 'weekly',
      loading: 'async',
      callback: CALLBACK_NAME,
    });
    const windowWithCallback = window as unknown as Record<string, unknown>;
    windowWithCallback[CALLBACK_NAME] = () => {
      delete windowWithCallback[CALLBACK_NAME];
      resolve();
    };
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      bootstrapPromise = null;
      reject(new Error('The Google Maps JavaScript API could not load'));
    };
    document.head.append(script);
  });
  return bootstrapPromise;
}

export type GoogleMapsLibraries = {
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
};

/**
 * Load the Maps JavaScript API plus the libraries the Live Map uses.
 * @returns {Promise<GoogleMapsLibraries>}
 */
export async function loadGoogleMaps(): Promise<GoogleMapsLibraries> {
  await bootstrapGoogleMaps();
  const [maps, marker] = await Promise.all([
    google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
    google.maps.importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
  ]);
  return { maps, marker };
}
